import { useEffect } from 'react';

/**
 * SEO Head Component
 * Manages canonical URLs, page titles, and meta descriptions dynamically
 * WITHOUT external dependencies (no react-helmet)
 *
 * Canonical policy:
 *   - The canonical <link> is emitted ONLY when an explicit canonicalPath is
 *     provided. Non-indexable pages (auth, admin, judge, profile, payment,
 *     test, register-closed…) pass no canonicalPath and therefore get NO
 *     canonical tag, and any residual tag left by a previous page is removed.
 *   - The host is HARDCODED to https://wuma.photo on purpose: the canonical
 *     must always point to the production domain even when the page is crawled
 *     on another origin (e.g. *.emergent.host). Never derived from
 *     location.origin / window.location.host.
 */
const CANONICAL_BASE_URL = "https://wuma.photo";

const SEOHead = ({
  title,
  description,
  canonicalPath,
  noindex = false,
  ogImage = "https://wuma.photo/images/og-image.jpg"
}) => {
  const fullCanonicalUrl = canonicalPath ? `${CANONICAL_BASE_URL}${canonicalPath}` : null;

  useEffect(() => {
    // Update page title
    if (title) {
      document.title = title;
    }

    // robots noindex — set for non-indexable / not-yet-revealed pages, removed otherwise.
    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, follow');
    } else if (robots) {
      robots.remove();
    }

    // Canonical: emit only when an explicit canonicalPath is provided.
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (fullCanonicalUrl) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', fullCanonicalUrl);
    } else if (canonicalLink) {
      // Non-indexable page: strip any residual canonical from a previous route.
      canonicalLink.remove();
    }

    // Update meta description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
    }

    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) {
      ogTitle.setAttribute('content', title);
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && description) {
      ogDescription.setAttribute('content', description);
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && fullCanonicalUrl) {
      ogUrl.setAttribute('content', fullCanonicalUrl);
    }

    let ogImageTag = document.querySelector('meta[property="og:image"]');
    if (ogImageTag && ogImage) {
      ogImageTag.setAttribute('content', ogImage);
    }

    return () => {
      // On unmount from a non-indexable page, remove the residual canonical tag.
      if (!fullCanonicalUrl) {
        const stale = document.querySelector('link[rel="canonical"]');
        if (stale) stale.remove();
      }
      // Remove residual robots noindex left by this page.
      if (noindex) {
        const staleRobots = document.querySelector('meta[name="robots"]');
        if (staleRobots) staleRobots.remove();
      }
    };
  }, [title, description, fullCanonicalUrl, ogImage, noindex]);

  return null; // This component doesn't render anything
};

export default SEOHead;
